
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DeleteButtonProps {
  quotation: any;
  onDelete?: (quotation: any) => void;
}

export function DeleteButton({ quotation, onDelete }: DeleteButtonProps) {
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteQuotation = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("quotations")
        .delete()
        .eq("id", quotation.id);

      if (error) throw error;

      toast({
        title: "Orçamento excluído",
        description: `O orçamento ${quotation.code} foi excluído com sucesso.`,
      });
      
      setShowDeleteDialog(false);
      
      // Call the onDelete callback if provided
      if (onDelete) {
        onDelete(quotation);
      }
    } catch (error: any) {
      toast({
        title: "Erro ao excluir",
        description: error.message || "Não foi possível excluir o orçamento.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setShowDeleteDialog(true)}
        className="hover:bg-[#E60026] hover:text-white"
        title="Excluir orçamento"
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#1A1F2C] border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-['Russo_One']">Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Você está prestes a excluir o orçamento <span className="font-bold text-white">{quotation.code}</span>.
              <br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border border-gray-600 text-white hover:bg-gray-700"
              disabled={isDeleting}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteQuotation}
              className="bg-[#E60026] hover:bg-[#cc0022] text-white"
              disabled={isDeleting}
            >
              {isDeleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
